/**
 * Sentry Error Tracking Configuration
 *
 * Provides error tracking and session replay for the frontend.
 * Only active in production unless debug mode is enabled.
 *
 * Usage:
 *   import { initSentry, captureImportError, captureOnboardingBlocked } from '@/lib/sentry';
 *
 *   // Initialize (call once in App.tsx)
 *   initSentry();
 *
 *   // Capture specific events
 *   captureOnboardingBlocked('no_organizations', { userId: '123' });
 *   captureImportError(error, 'my-org', false);
 */

// Note: @sentry/react needs to be installed: npm install @sentry/react
// This file provides the interface; actual initialization requires the package

import type { AnalyticsUser } from '@/lib/analytics/types';
import type { TranslationGuardEvent, TranslationGuardMethod } from '@/lib/browserCompat';
import { isSyntheticClient } from '@/lib/syntheticClient';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
const IS_PRODUCTION = import.meta.env.MODE === 'production';
const DEBUG_MODE = typeof window !== 'undefined' && localStorage.getItem('sentry_debug') === 'true';
const IS_LOCALHOST = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

type SentryModule = typeof import('@sentry/react');

let sentryInitialized = false;
let Sentry: SentryModule | null = null;

/**
 * Initialize Sentry error tracking
 * Call this once in App.tsx before React renders
 */
export async function initSentry(): Promise<void> {
  if (sentryInitialized) return;

  if (!SENTRY_DSN) {
    return;
  }

  if (!IS_PRODUCTION && !DEBUG_MODE) {
    return;
  }

  // Crawlers, synthetic monitors and our own prerenderer never get Sentry at
  // all. They cannot report a bug and their replays are unwatchable, so the
  // ~136kB gzip download, its main-thread init and the Sentry quota it spends
  // are pure waste. Marked initialized so a later call does not retry.
  if (isSyntheticClient()) {
    sentryInitialized = true;
    return;
  }

  try {
    // Dynamic import to avoid bundling Sentry in dev builds when not needed
    Sentry = await import('@sentry/react');

    Sentry.init({
      dsn: SENTRY_DSN,
      release: SENTRY_RELEASE,
      environment: IS_LOCALHOST ? 'local' : import.meta.env.MODE,
      // Replay stays here, eagerly, on purpose. Moving it to
      // lazyLoadIntegration() (CDN-loaded on demand) was tried and reverted: it
      // saved NOTHING. @sentry/react re-exports @sentry/browser wholesale and
      // that index does `export { replayIntegration } from
      // '@sentry-internal/replay'`, so the recorder ships in our chunk however
      // it is referenced. Two builds confirmed it - removing this line, and
      // then also narrowing the namespace to explicit property reads, both left
      // the chunk byte-identical at 406,631 bytes raw / 136kB gzip. Lazy
      // loading would therefore have kept the bytes AND added a sentry-cdn
      // round trip that ad blockers routinely break.
      //
      // Replays MUST mask all text and block media: the dashboards render
      // customer personal data (developer names, PR titles, review comments),
      // and the DPA (docs/legal/dpa.md, Annex III) commits that error-session
      // replays carry on-screen text masked. Pinned by sentry.test.ts.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      // Performance monitoring
      tracesSampleRate: 0.1, // 10% of transactions

      // Session replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Only send errors in production
      enabled: IS_PRODUCTION || DEBUG_MODE,

      // Filter out known browser extension false positives
      ignoreErrors: [
        // Kaspersky, AVG, Avast security extensions
        /Object Not Found Matching Id/,
        // Chrome/Firefox extension errors
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        // Generic extension promise rejections with no useful info
        /Non-Error promise rejection captured with value/,
        // Legacy/bot browsers (e.g. Chrome 79 scrapers) lack Array/String.prototype.at,
        // so third-party scripts like PostHog's web-vitals extension throw this. We
        // polyfill it in browserCompat.ts; filter residual reports as defense-in-depth.
        // Ref: Sentry CODEPULSE-BACKEND-C4.
        /\.at is not a function/,
      ],

      beforeSend(event, hint) {
        const error = hint?.originalException;
        // Filter out expected HTTP errors - these are application-level,
        // not crashes. Components handle 403/404 in their catch blocks.
        if (error && typeof error === 'object' && 'isAxiosError' in error) {
          const axiosError = error as { response?: { status?: number }; config?: { url?: string } };
          const status = axiosError.response?.status;
          if (status === 403 || status === 404) {
            return null;
          }
          // Expected OAuth failures: GitHub rejects an expired or already-used
          // authorization code with a 4xx on the auth callback. The UI already
          // handles this (catches the error and redirects to /login) - it's user
          // behaviour (slow consent, refresh/back), not a crash. Ref: Sentry
          // CODEPULSE-BACKEND-CA.
          //
          // Dropping the exception entirely made a genuine regression here
          // invisible - a rotated client secret or a bug isolated to new-user
          // installs would also produce a 400/401 and get silently discarded
          // alongside the expected ones. Keep a low-noise, grouped rate signal
          // (one issue, count trending) instead of zero signal.
          const requestUrl = axiosError.config?.url ?? '';
          if ((status === 400 || status === 401) && requestUrl.includes('/auth/')) {
            captureAuthRejection({ status, url: requestUrl });
            return null;
          }
          // Any other 401 means the session ended. The API client owns that
          // (redirect under a ProtectedRoute, silent sign-out on a public page);
          // the rejection a public page's caller then logs is not a crash.
          if (status === 401) {
            return null;
          }
        }

        // Filter out browser extension and non-app errors
        const errorMessage = error instanceof Error ? error.message : String(error ?? '');
        if (
          errorMessage.includes('runtime.sendMessage()') ||
          errorMessage.includes('ResizeObserver loop')
        ) {
          return null;
        }

        // Filter errors originating from browser extensions
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (frames?.some(f =>
          f.filename?.startsWith('chrome-extension://') ||
          f.filename?.startsWith('moz-extension://') ||
          f.filename?.startsWith('safari-extension://') ||
          // PostHog's lazily-loaded web-vitals extension throws on legacy/bot
          // engines (e.g. `.at` is not a function on Chrome 79). Not our code;
          // pure noise. Ref: Sentry CODEPULSE-BACKEND-C4.
          /\/web-vitals(\.[a-z0-9]+)?\.js/i.test(f.filename ?? '')
        )) {
          return null;
        }

        // Is any frame from our own bundle? @sentry/browser's stack parser
        // hardcodes `in_app: true` on EVERY frame ("All browser frames are
        // considered in_app" - stack-parsers.js), so in_app cannot distinguish
        // our code from an injected third-party script. Match on the filename
        // instead: production frames are /assets/*.js, and an inline script
        // reports as the document URL on our own origin.
        const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
        const hasAppFrame = () => frames?.some(f => {
          const filename = f.filename ?? '';
          return filename.includes('/assets/') ||
            (appOrigin !== '' && filename.startsWith(appOrigin));
        }) ?? false;

        // Filter errors thrown entirely within Microsoft Clarity's own bundle.
        // Clarity loads as an async third-party script (clarity.ms/tag/...); its
        // minified runtime occasionally throws unhandled rejections (e.g. "Cannot
        // read properties of null (reading 'sequence')") that surface via
        // onunhandledrejection with every frame in /<version>/clarity.js and no
        // first-party frame. Not our code, unactionable. We keep the event if our
        // own bundle is on the stack. Ref: Sentry CODEPULSE-BACKEND-2VM.
        const hasClarityFrame = frames?.some(f => {
          const filename = f.filename ?? '';
          return filename.includes('clarity.js') || filename.includes('clarity.ms');
        });
        if (hasClarityFrame && !hasAppFrame()) {
          return null;
        }

        // Filter "Maximum call stack size exceeded" with no first-party frame.
        // In-page translators (Google Translate, iOS/in-app-browser translation)
        // inject an anonymous script whose DOM-mutation/MutationObserver loop
        // recurses and overflows the stack; it surfaces via window.onerror with a
        // single `undefined:<line>:<col>` frame (no source URL) and never touches
        // our bundle. A genuine recursion in our code carries a first-party frame
        // and still passes through. Ref: Sentry CODEPULSE-BACKEND-EX.
        if (errorMessage.includes('Maximum call stack size exceeded') && !hasAppFrame()) {
          return null;
        }

        return event;
      },
    });

    sentryInitialized = true;
  } catch (_error) {
    return;
  }
}

/**
 * Set user and org context for Sentry
 */
export function setSentryUser(user: AnalyticsUser): void {
  if (!Sentry || !sentryInitialized) return;

  Sentry.setUser({
    id: user.userId,
    email: user.email,
    username: user.githubLogin || undefined,
  });

  Sentry.setTag('org_id', user.orgId || '');
  Sentry.setTag('org_slug', user.orgSlug || '');
  Sentry.setTag('org_name', user.orgName || '');
  Sentry.setTag('subscription_tier', user.subscriptionTier);
  Sentry.setTag('is_trial', String(user.isTrial));
  Sentry.setTag('is_personal', user.isPersonal != null ? String(user.isPersonal) : '');
}

/**
 * Update org-related Sentry tags (e.g., on org switch)
 */
export function updateSentryOrgContext(user: Partial<AnalyticsUser>): void {
  if (!Sentry || !sentryInitialized) return;

  if (user.orgId != null) Sentry.setTag('org_id', user.orgId);
  if (user.orgSlug != null) Sentry.setTag('org_slug', user.orgSlug);
  if (user.orgName != null) Sentry.setTag('org_name', user.orgName);
  if (user.subscriptionTier != null) Sentry.setTag('subscription_tier', user.subscriptionTier);
  if (user.isTrial != null) Sentry.setTag('is_trial', String(user.isTrial));
  if (user.isPersonal != null) Sentry.setTag('is_personal', String(user.isPersonal));
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser(): void {
  if (!Sentry || !sentryInitialized) return;

  Sentry.setUser(null);
  Sentry.setTag('org_id', '');
  Sentry.setTag('org_slug', '');
  Sentry.setTag('org_name', '');
  Sentry.setTag('subscription_tier', '');
  Sentry.setTag('is_trial', '');
  Sentry.setTag('is_personal', '');
}

/**
 * Capture when user is blocked during onboarding
 */
export function captureOnboardingBlocked(
  reason: string,
  context?: Record<string, unknown>
): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  Sentry.captureMessage('Onboarding Blocked', {
    level: 'warning',
    tags: {
      category: 'onboarding',
      blocked_reason: reason,
    },
    extra: context,
  });
}

/**
 * Capture when a loading spinner stays visible far longer than expected.
 * Silent stuck-loading states throw no exception; this is our only signal.
 * See docs/superpowers/specs/2026-07-19-stuck-spinner-watchdog-design.md
 */
export function captureStuckSpinner(context: {
  route: string;
  spinnerLabel: string;
  elapsedMs: number;
  visibility: string;
}): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  Sentry.captureMessage('Stuck loading spinner', {
    level: 'warning',
    tags: {
      category: 'stuck_spinner',
      route: context.route,
      spinner_label: context.spinnerLabel,
    },
    extra: { ...context },
    fingerprint: ['stuck-spinner', context.route, context.spinnerLabel],
  });
}

/**
 * Capture a request that never received an HTTP response - a timeout or a
 * network failure. These throw no server error yet are exactly what strands a
 * user on a spinner. Grouped per (category, method, url) so a flaky endpoint is
 * one issue, not thousands.
 */
export function captureRequestFailure(context: {
  category: 'api_timeout' | 'api_network';
  method: string;
  url: string;
  code?: string;
  // The axios error message. For api_timeout it is the one signal that tells a
  // genuine timeout ("timeout of 8000ms exceeded") apart from a client-side
  // abort ("Request aborted"/"canceled" when the visitor closes the tab) - both
  // of which axios reports with code ECONNABORTED. Carries no URL/PII.
  message?: string;
}): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  const verb = context.category === 'api_timeout' ? 'timed out' : 'failed (network)';
  Sentry.captureMessage(`Request ${verb}: ${context.method} ${context.url}`, {
    level: 'warning',
    tags: { category: context.category, method: context.method },
    extra: { ...context },
    fingerprint: [context.category, context.method, context.url],
  });
}

/**
 * Capture a 400/401 rejection from an /auth/* endpoint (e.g. GitHub rejecting
 * an expired or already-used authorization code) as a low-noise, grouped
 * signal instead of dropping it entirely. beforeSend() filters the underlying
 * exception because most of these are ordinary user behaviour (slow consent,
 * refresh/back), but a genuine regression that breaks the OAuth code exchange
 * - e.g. for new-user installs specifically - would look identical and must
 * still be visible as a rate/count trend. Grouped per (status, url) so this
 * is one issue with an event count, not per-occurrence noise.
 */
export function captureAuthRejection(context: { status: number; url: string }): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  Sentry.captureMessage(`Auth request rejected: ${context.status} ${context.url}`, {
    level: 'warning',
    tags: { category: 'auth_rejected', status: String(context.status) },
    extra: { ...context },
    fingerprint: ['auth_rejected', String(context.status), context.url],
  });
}

// Methods already reported this page load. The guard can fire dozens of times
// while a translator rewrites a page, and each report is the same fact.
const reportedTranslationGuards = new Set<TranslationGuardMethod>();

/**
 * Record that installTranslationDomGuard() had to step in because a browser
 * translator moved a node out from under React.
 *
 * The guard exists to swallow a crash, which also makes it silent: with no
 * signal we cannot tell "the guard is working" from "nobody translates our
 * pages". This is that signal. It is `info`, not an error, because the guard
 * did its job - the page survived - and it is sent ONCE per method per page
 * load, so the issue's event count is "translated sessions that hit it", not
 * DOM churn.
 *
 * The tags exist to answer questions before anyone changes the fallback:
 * `parent_state`/`new_parent` say whether the node was wrapped (an ancestor to
 * insert before exists) or detached (none does), and `translated` says whether
 * the browser had actually flagged the document as translated, i.e. whether
 * translation is really what triggered it. Adds a breadcrumb too, so a later
 * error in the same session shows it happened.
 */
export function captureTranslationDomGuard(event: TranslationGuardEvent): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }
  if (reportedTranslationGuards.has(event.method)) {
    return;
  }
  reportedTranslationGuards.add(event.method);

  const html = document.documentElement;
  const translated =
    html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl');
  const newParent = event.newParent ?? 'none';

  addBreadcrumb('Translation DOM guard fired', 'translation', 'info', {
    method: event.method,
    node: event.node,
    parent_state: event.parentState,
    new_parent: newParent,
  });

  Sentry.captureMessage(`Translation DOM guard fired: ${event.method}`, {
    level: 'info',
    tags: {
      category: 'translation_dom_guard',
      method: event.method,
      parent_state: event.parentState,
      new_parent: newParent,
      translated: String(translated),
    },
    extra: {
      node: event.node,
      html_lang: html.lang,
      user_language: typeof navigator !== 'undefined' ? navigator.language : '',
    },
    fingerprint: ['translation_dom_guard', event.method],
  });
}

/**
 * Capture import errors during onboarding
 */
export function captureImportError(
  error: Error,
  orgLogin: string,
  isPersonal: boolean
): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  Sentry.captureException(error, {
    tags: {
      category: 'import',
      org_login: orgLogin,
      is_personal: String(isPersonal),
    },
  });
}

/**
 * Capture generic errors with context
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!Sentry || !sentryInitialized) {
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, unknown>
): void {
  if (!Sentry || !sentryInitialized) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Test Sentry integration by sending a test error and message.
 * Call from browser console: window.__testSentry()
 */
export function testSentry(): void {
  if (!Sentry || !sentryInitialized) {
    console.warn('[Sentry] Not initialized. Set localStorage sentry_debug=true and reload, or check VITE_SENTRY_DSN.');
    return;
  }

  Sentry.captureMessage('Sentry test message from CodePulse frontend', { level: 'info' });
  Sentry.captureException(new Error('Sentry test error from CodePulse frontend'));
  console.info('[Sentry] Test error and message sent. Check your Sentry dashboard.');
}

// Expose on window for easy console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__testSentry = testSentry;
}

export { Sentry };
